import React, { useState } from "react";
import {
  getFormattedEventDistanceFromNow,
  getIssueCreatedAt,
  getIssueUpdatedAt,
} from "../../../utils/IntegrationsManagementUtils";
import { useHistory } from "react-router-dom";
import { navigateToIndividualIssuePage } from "../../../../../router/RouterUtils";
import { Button, ButtonVariant, Tooltip, Text } from "@merge-api/merge-javascript-shared";
import { MessageSquare, Check, VolumeX, ChevronRight, Volume1 } from "lucide-react";
import { updateIntegrationIssueStatus } from "../../../utils/IntegrationsManagementAPIClient";
import { IntegrationIssueStatus } from "../../../IntegrationsManagementEntities";
import { showSuccessToast, showErrorToast } from "../../../../shared/Toasts";
import { IntegrationIssueCommentModal } from "../../../issues/detail-page/IntegrationIssueCommentModal";
import { muteIntegrationIssue } from "../../../utils/IntegrationsManagementAPIClient";
import { getLastActionMessage } from "../../../issues/IssuesPage";
import { IntegrationIssue } from "../../../IntegrationsManagementEntities";
import { isIssueMuted } from "../../../utils/IntegrationsManagementUtils";
import IssueStatusBadge from "../../../../shared/IssueStatusBadge";

type IssuePageProps = {
  linkedAccountIssue: IntegrationIssue;
};

export const getMuteFor1DayTime = () => {
  const now = new Date();
  now.setHours(24);
  return now;
};
const IssuesPageRow = ({ linkedAccountIssue }: IssuePageProps) => {
  const history = useHistory();
  const [integrationIssue, setIntegrationIssue] = useState(linkedAccountIssue);
  const [isCommentModalShown, setIsCommentModalShown] = useState(false);

  const isMuted = isIssueMuted(integrationIssue);
  return (
    <tr
      className="hover:cursor-pointer hover:bg-gray-0"
      onClick={() => {
        if (!isCommentModalShown) {
          navigateToIndividualIssuePage(history, integrationIssue.id, "linked_account_page");
        }
      }}
    >
      <td colSpan={2}>
        <div className="flex flex-row items-center whitespace-nowrap">
          <Text variant="h6" className={isMuted ? "text-gray-40" : ""}>
            {linkedAccountIssue.error_description}
          </Text>
          {isMuted && <VolumeX className="ml-2 text-gray-40" size={16} />}
        </div>
      </td>
      <td>
        <IssueStatusBadge issue={integrationIssue} />
      </td>
      <td>{getLastActionMessage(linkedAccountIssue.last_action, isMuted)}</td>
      <td>
        <Text className="text-gray-40">
          {getFormattedEventDistanceFromNow(getIssueUpdatedAt(integrationIssue))}
        </Text>
      </td>
      <td>
        <Text className="text-gray-40">
          {getFormattedEventDistanceFromNow(getIssueCreatedAt(integrationIssue))}
        </Text>
      </td>
      <td>
        <div className="flex items-center justify-end gap-3">
          <Tooltip title="View comments">
            <Button
              size="sm"
              variant={ButtonVariant.IconOnly}
              onClick={(e) => {
                e.stopPropagation();
                setIsCommentModalShown(!isCommentModalShown);
              }}
            >
              <MessageSquare size={16} />
            </Button>
          </Tooltip>
          {integrationIssue.status === IntegrationIssueStatus.ONGOING && (
            <>
              {isMuted ? (
                <Tooltip title="Unmute ongoing issue">
                  <Button
                    size="sm"
                    variant={ButtonVariant.IconOnly}
                    onClick={(e) => {
                      e.stopPropagation();
                      muteIntegrationIssue({
                        integrationIssueID: integrationIssue.id,
                        muted_until: undefined,
                        mute_incident_count: undefined,
                        onSuccess: (updatedIntegrationIssue) => {
                          setIntegrationIssue(updatedIntegrationIssue);
                          showSuccessToast("Successfully unmuted issue");
                        },
                        onFailure: () => {
                          showErrorToast("Error occurred while muting issue");
                        },
                      });
                    }}
                  >
                    <Volume1 size={16} />
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title="Mute ongoing issue">
                  <Button
                    size="sm"
                    variant={ButtonVariant.IconOnly}
                    onClick={(e) => {
                      e.stopPropagation();
                      muteIntegrationIssue({
                        integrationIssueID: integrationIssue.id,
                        muted_until: getMuteFor1DayTime(),
                        onSuccess: (updatedIntegrationIssue) => {
                          setIntegrationIssue(updatedIntegrationIssue);
                          showSuccessToast("Successfully muted issue for 24 hours");
                        },
                        onFailure: () => {
                          showErrorToast("Error occurred while muting issue");
                        },
                      });
                    }}
                  >
                    <VolumeX size={16} />
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Resolve ongoing issue">
                <Button
                  size="sm"
                  variant={ButtonVariant.IconOnly}
                  onClick={(e) => {
                    e.stopPropagation();
                    updateIntegrationIssueStatus({
                      integrationIssueID: integrationIssue.id,
                      status: IntegrationIssueStatus.RESOLVED,
                      onSuccess: (updatedIntegrationIssue) => {
                        setIntegrationIssue(updatedIntegrationIssue);
                        showSuccessToast("Updated Integration Issue");
                      },
                      onError: () => {
                        showErrorToast("Unable To Update Integration Issue");
                      },
                    });
                  }}
                >
                  <Check size={16} />
                </Button>
              </Tooltip>
            </>
          )}
          <div className="ml-auto">
            <Tooltip title="See more details">
              <ChevronRight
                size={16}
                className="text-gray-40"
                onClick={() => {
                  navigateToIndividualIssuePage(
                    history,
                    integrationIssue.id,
                    "linked_account_page",
                  );
                }}
              />
            </Tooltip>
          </div>
        </div>
      </td>
      <IntegrationIssueCommentModal
        integrationIssue={integrationIssue}
        isShown={isCommentModalShown}
        setIsShown={setIsCommentModalShown}
      />
    </tr>
  );
};
export default IssuesPageRow;
